import React, { useState, useRef } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import api from '../../services/api';
import { extractApiError } from '../../utils/helpers';

/**
 * Bild-Feld mit Upload + URL-Eingabe. Beim Upload konvertiert das Backend
 * jedes Format (PNG/JPG/HEIC/…) zu WebP und gibt eine saubere absolute
 * URL zurück, die als image_url gesetzt wird. Externe Links lassen sich
 * weiterhin direkt ins Textfeld einfügen.
 *
 * Props: value (string), onChange (fn(url)).
 */
const ImageUploadField = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await api.media.upload(file);
      onChange(url);
      toast.success('Bild hochgeladen & ins richtige Format konvertiert');
    } catch (err) {
      toast.error(extractApiError(err, 'Upload fehlgeschlagen'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…  oder  Bild hochladen →"
          className="flex-1"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 px-3 rounded-md border border-gray-300 dark:border-slate-600 text-sm font-medium hover:bg-gray-50 dark:hover:bg-slate-800 disabled:opacity-60 whitespace-nowrap"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? 'Lädt…' : 'Hochladen'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Optional. Jedes Format (auch HEIC/iPhone) wird automatisch zu WebP konvertiert.
      </p>
      {value && (
        <img
          src={value}
          alt="Bild-Vorschau"
          className="max-h-32 rounded-lg border border-gray-200 dark:border-slate-700 object-contain bg-gray-50 dark:bg-slate-800/60"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      )}
    </div>
  );
};

export default ImageUploadField;
